import React, { useCallback, useMemo } from 'react';
import { Layout, Row, Col, Card, Table, Button, Popconfirm, Empty } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	markWalkthroughCompleteForBuyer,
	markWalkthroughCompleteForSupplier,
	updateWalkthroughTaskAnswer,
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import {
	getObjectValues,
	getWalkthroughScoreObject,
	nullSafeGet,
	nullSafeGetOrElse,
	onRowWithRangeSelection,
} from '../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { groupBy } from 'lodash';
import TaskEditPopup from '../common/TaskEditPopup';
import { useEffect, useState } from 'react';
import OWInsightPopover from '../ow_insight_popover/OWInsightPopover';
import { UNFINISHED_WORK_ORDER_DISPLAY_STATUS_DISPLAY_NAMES } from '../../constants/WorkOrderDisplayStatuses';
import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';
import { UserName } from '../name_component/user/UserNameComponent';
import LocationName from '../name_component/location/LocationNameComponent';
import DateTimeHover from '../date_time_component/DateTime';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import WalkthroughScoreDisplay from '../walkthrough_score_display/WalkthroughScoreDisplay';
import TaskAnswerDetails from '../task_answer_details/TaskAnswerDetails';
import { withUserType } from '../withUserType';
import {
	SITE_SURVEY_ANSWER_TYPE,
	isWalthroughTaskAnswered,
	validScoreAvailable,
} from '../../utils/SiteSurveyUtils';
import GroupedTaskDisplay from '../ow_inline_todo_list/GroupedTaskDisplay';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getAnswerItems } from '../../utils/TaskItemUtils';
import WalkthroughTaskListAnswerDisplay from './WalkthroughTaskListAnswerDisplay';
import WalkthroughTaskListScoreDisplay, {
	GroupedTaskTaskListScoreDisplay,
} from '../walkthrough_score_display/WalkthroughTaskListScoreDisplay';
import {
	GroupedTaskUnansweredDisplay,
	TaskGroupProgressCircleWithPopover,
} from '../walkthrough_score_display/TaskGroupProgressCircle';

const { Content } = Layout;

require('./WalkthroughsDetailDetailsPage.less');

const sanitizeTaskAnswerTitle = (groupedAnswers) =>
	Object.keys(groupedAnswers).reduce((acc, key) => {
		acc[key] = (groupedAnswers[key] || []).map((answer, idx) =>
			answer.titleId ? answer : { ...answer, titleId: `${key}-${answer.title}-${idx}` }
		);
		return acc;
	}, {});

const WalkthroughsDetailDetailsPage = ({
	getWalkthrough,
	updateTaskAnswer,
	markWalkthroughComplete,
	walkthroughsFetching,
	walkthrough,
	match,
	userType,
}) => {
	const walkthroughId = match.params.id;
	const [loading, setLoading] = useState(true);
	const [expandedRowKeys, setExpandedRowKeys] = useState(new Set());
	const [unansweredOnlyGroups, setUnansweredOnlyGroups] = useState(new Set<string>());
	const [showMarkThisComplete, setShowMarkThisComplete] = useState(true);
	const [showEditTaskAnswer, setShowEditTaskAnswer] = useState(false);
	const [currentTaskAnswer, setCurrentTaskAnswer] = useState({});

	useEffect(() => {
		getWalkthrough(walkthroughId).then(() => setLoading(false));
	}, []);

	const toggleExpandByTitle = (title) => {
		expandedRowKeys.has(title) ? expandedRowKeys.delete(title) : expandedRowKeys.add(title);

		setExpandedRowKeys(new Set(expandedRowKeys));
	};

	const onExpandIconClick = (title) => {
		toggleExpandByTitle(title);
	};

	const onExpand = (expanded, record) => {
		toggleExpandByTitle(record.titleId);
	};

	const completeWalkthrough = useCallback(() => {
		markWalkthroughComplete(walkthrough.id).then(() => {
			getWalkthrough(walkthrough.id);
		});
	}, [getWalkthrough, markWalkthroughComplete, walkthrough]);

	const handleEdit = (taskAnswer) => {
		setCurrentTaskAnswer(taskAnswer);
		setShowEditTaskAnswer(true);
	};

	const hideTaskAnswerPopup = () => {
		setCurrentTaskAnswer({});
		setShowEditTaskAnswer(false);
	};

	const isWalkthroughComplete = useMemo(
		() => walkthrough.status && walkthrough.status === 'complete',
		[walkthrough.status]
	);
	const overallScoreObject = useMemo(() => getWalkthroughScoreObject(walkthrough), [walkthrough]);

	const taskListAvailable = useMemo(
		() => nullSafeGetOrElse('taskAnswers', walkthrough, []).flatMap(getAnswerItems).length > 0,
		[walkthrough]
	);

	const walkthroughDetails = useMemo(
		() =>
			loading
				? {}
				: {
						'Walkthrough ID': `# ${nullSafeGet('id', walkthrough)}`,
						'Site Survey': nullSafeGet('siteSurvey.title', walkthrough),
						Location: (
							<LocationName
								mode={'inline'}
								location={nullSafeGet('location', walkthrough)}
								placement={'top'}
							/>
						),
						Created: (
							<DateTimeHover
								timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
								timestamp={nullSafeGet('createdAt', walkthrough)}
							/>
						),
						...(walkthrough.startTime
							? {
									['Started At']: (
										<DateTimeHover
											timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
											timestamp={nullSafeGet('startTime', walkthrough)}
										/>
									),
							  }
							: {}),
						Due: nullSafeGet('dueAt', walkthrough) ? (
							<DateTimeHover
								timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
								timestamp={nullSafeGet('dueAt', walkthrough)}
							/>
						) : null,
						...(walkthrough.endTime
							? {
									['Ended At']: (
										<DateTimeHover
											timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
											timestamp={nullSafeGet('endTime', walkthrough)}
										/>
									),
							  }
							: {}),
						...(walkthrough.status === 'complete' && walkthrough.completedAt
							? {
									Completed: (
										<DateTimeHover
											timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
											timestamp={nullSafeGet('completedAt', walkthrough)}
										/>
									),
							  }
							: {}),
						...(walkthrough.completedByContact
							? {
									'Completed By': (
										<UserName
											contact={walkthrough.completedByContact}
											mode={'inline'}
											placement={'top'}
										/>
									),
							  }
							: {}),
						...(validScoreAvailable(overallScoreObject) && {
							Score: (
								<WalkthroughScoreDisplay
									scoreObject={overallScoreObject}
									walkthrough={walkthrough}
								/>
							),
							...(taskListAvailable && {
								Tasks: <WalkthroughTaskListScoreDisplay walkthrough={walkthrough} />,
							}),
						}),
				  },
		[loading, overallScoreObject, taskListAvailable, walkthrough]
	);

	const filteredWalkthroughDetails = useMemo(
		() =>
			Object.keys(walkthroughDetails || {}).reduce((acc, el) => {
				const val = walkthroughDetails[el];
				if (val !== null && val !== 'undefined' && val !== undefined && val !== '') {
					acc[el] = walkthroughDetails[el];
				}
				return acc;
			}, {}),
		[walkthroughDetails]
	);

	const expandRowRender = (record) => {
		return <TaskAnswerDetails record={record} walkthrough={walkthrough} />;
	};

	const expandAll = () => {
		const titleIds = getObjectValues(groupedFilteredTaskAnswers)
			.flatMap((record) => record)
			.map((record) => record.titleId);
		setExpandedRowKeys(new Set(titleIds));
	};
	const collapseAll = () => {
		setExpandedRowKeys(new Set());
	};

	const getAnswerForNonMultipleChoiceQuestion = useCallback((record) => {
		switch (record.questionType) {
			case SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE:
			case SITE_SURVEY_ANSWER_TYPE.TASK_LIST:
			case SITE_SURVEY_ANSWER_TYPE.CHECKLIST:
				return null;
			default:
				return (
					<div className="walkthroughRowDisplay__rowTitle mt-2 text-gray-600">
						<div className="walkthroughRowDisplay__rowInlineGroup">
							{nullSafeGetOrElse('answer', record, '--')}
						</div>
					</div>
				);
		}
	}, []);

	const getOtherAnswers = useCallback((record) => {
		switch (record.questionType) {
			case SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE:
				return (
					<div className="walkthroughRowDisplay__rowSubtitle ml-2 text-gray-600">
						<div className="walkthroughRowDisplay__rowInlineGroup">
							{nullSafeGetOrElse('answer', record, '--')}
						</div>
					</div>
				);
			case SITE_SURVEY_ANSWER_TYPE.TASK_LIST:
			case SITE_SURVEY_ANSWER_TYPE.CHECKLIST:
				return (
					<WalkthroughTaskListAnswerDisplay
						taskAnswer={record}
						timeZoneId={nullSafeGet('location.timeZoneId', walkthrough)}
						checkable={!isWalkthroughComplete}
					/>
				);
			default:
				return null;
		}
	}, []);

	const walkthroughColumns = [
		{
			title: 'Question',
			dataIndex: 'task',
			render: (text, record) => (
				<div className="cursor-pointer">
					<div className="walkthroughRowDisplay__rowTitle text-gray-500">
						<Ellipsis style={{ wordBreak: 'normal' }} tooltip={true} length={64}>
							{nullSafeGet('title', record)}
						</Ellipsis>
					</div>
					{nullSafeGet('description', record) && (
						<div className="walkthroughRowDisplay__rowSubtitle text-gray-400">
							{record.description}
						</div>
					)}
					{getAnswerForNonMultipleChoiceQuestion(record)}
				</div>
			),
		},
		{
			title: 'Question',
			dataIndex: 'task',
			render: (text, record) => {
				const numComments = record.comments ? 1 : 0;
				const attachments = nullSafeGetOrElse('attachments', record, []);
				const numAttachments = attachments.length;
				return (
					<div className="flex cursor-pointer flex-row items-center">
						<div
							className={'icons'}
							style={{
								display: 'flex',
								gap: 16,
								flexGrow: 1,
								justifyContent: 'flex-end',
								alignItems: 'center',
							}}
						>
							{numComments > 0 ? (
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
									}}
								>
									<div
										style={{
											minWidth: 10,
											margin: '0px',
										}}
									>
										{numComments}
									</div>
									<i
										style={{
											fontSize: 20,
											verticalAlign: 'middle',
											cursor: 'pointer',
											color: '#999',
											margin: '0px 2px',
											paddingLeft: '4px',
											paddingRight: '4px',
										}}
										className="icons8-font icons8-speech-bubble"
									/>
								</div>
							) : null}
							{numAttachments > 0 ? (
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
									}}
								>
									<div
										style={{
											minWidth: 10,
											margin: '0px',
										}}
									>
										{numAttachments}
									</div>
									<i
										style={{
											fontSize: 20,
											verticalAlign: 'middle',
											cursor: 'pointer',
											color: '#999',
											margin: '0px 2px',
											paddingLeft: '4px',
											paddingRight: '4px',
										}}
										className="icons8-font icons8-compact-camera"
									/>
								</div>
							) : null}
							<OWInsightPopover
								title={'Work Orders'}
								breakdown={'displayStatus'}
								labels={UNFINISHED_WORK_ORDER_DISPLAY_STATUS_DISPLAY_NAMES}
								icon={'icons8-wrench'}
								defaultData={record.workOrders || []}
							/>
						</div>
					</div>
				);
			},
		},
		{
			title: 'Answer',
			dataIndex: 'task',
			render: (text, record) => (
				<div className="flex cursor-pointer flex-row items-center justify-start">
					{getOtherAnswers(record)}
				</div>
			),
		},
		{
			title: 'Action',
			dataIndex: 'task',
			render: (text, record) => (
				<div className="flex cursor-pointer flex-row items-center justify-end">
					{!isWalkthroughComplete ? (
						<div className="flex-end ml-2 flex items-center gap-x-3">
							<Button
								style={{ marginLeft: 8 }}
								key="new"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleEdit(record);
								}}
							>
								Edit
							</Button>
						</div>
					) : null}
					<span className="ml-3 text-gray-400">
						{expandedRowKeys.has(record.titleId) ? <DownOutlined /> : <RightOutlined />}
					</span>
				</div>
			),
		},
	];

	const updateAnswer = (answer) => {
		return new Promise((resolve, reject) => {
			updateTaskAnswer(walkthrough.id, answer)
				.then((res) => {
					setShowEditTaskAnswer(false);
					resolve(res);
				})
				.catch(reject);
		});
	};
	const taskAnswers =
		walkthrough.taskAnswers && walkthrough.taskAnswers.sort((a, b) => a.orderingId - b.orderingId);

	const canBeMarkedComplete =
		walkthrough.status &&
		walkthrough.status !== 'complete' &&
		nullSafeGetOrElse('taskAnswers', walkthrough, []).some(isWalthroughTaskAnswered);

	const groupedFilteredTaskAnswers = useMemo(
		() => taskAnswers && sanitizeTaskAnswerTitle(groupBy(taskAnswers, 'groupTitle')),
		[taskAnswers]
	);

	return walkthroughsFetching && !nullSafeGet('id', walkthrough) ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="walkthroughsDetailDetailsPage">
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				{showMarkThisComplete && canBeMarkedComplete && (
					<Col span={24}>
						<Card className="rowSpacing">
							<h5>Do you want to mark this walkthrough complete?</h5>
							<div style={{ marginTop: 16, marginBottom: 16 }}>
								<Popconfirm
									title="Are you sure you want to mark this walkthrough complete?"
									okText="Yes"
									cancelText="No"
									onConfirm={completeWalkthrough}
								>
									<Button type="primary" key={3} style={{ marginRight: 16 }} ghost={true}>
										Mark this complete
									</Button>
								</Popconfirm>
								<Button
									key="reminder"
									style={{ marginLeft: 16 }}
									onClick={() => setShowMarkThisComplete(false)}
								>
									No, this walkthrough is not complete
								</Button>
							</div>
						</Card>
					</Col>
				)}
				<Col span={24}>
					<Card title="Walkthrough Details" className="rowSpacing">
						<div style={{ padding: 0 }} className="materialCard__body">
							<div className="materialCard__supportingText">
								<KeyValueDisplay
									horizontal={true}
									keyWidth={120}
									keyValueStore={filteredWalkthroughDetails}
								/>
							</div>
						</div>
					</Card>
				</Col>
				<Col span={24} className={'rowSpacing'}>
					{walkthrough.taskAnswers && walkthrough.taskAnswers.filter((t) => t.answer).length > 0 ? (
						<Row justify={'end'}>
							<Button type={'text'} onClick={expandAll}>
								Expand All
							</Button>
							<Button type={'text'} onClick={collapseAll}>
								Collapse All
							</Button>
						</Row>
					) : null}
				</Col>
				<Col span={24}>
					{walkthrough.taskAnswers && walkthrough.taskAnswers.length > 0 ? (
						Object.keys(groupedFilteredTaskAnswers).map((gtaKey) => {
							const scoreObject = getWalkthroughScoreObject(walkthrough, gtaKey);
							const groupAnswers = groupedFilteredTaskAnswers[gtaKey];
							const unansweredOnly = unansweredOnlyGroups.has(gtaKey);
							const visibleAnswers = unansweredOnly
								? groupAnswers.filter((task) => !isWalthroughTaskAnswered(task))
								: groupAnswers;
							const enableUnansweredOnly = () => {
								const next = new Set(unansweredOnlyGroups);
								next.add(gtaKey);
								setUnansweredOnlyGroups(next);
							};
							const toggleUnansweredOnly = () => {
								const next = new Set(unansweredOnlyGroups);
								if (next.has(gtaKey)) {
									next.delete(gtaKey);
								} else {
									next.add(gtaKey);
								}
								setUnansweredOnlyGroups(next);
							};
							const disableUnansweredOnly = () => {
								const next = new Set(unansweredOnlyGroups);
								next.delete(gtaKey);
								setUnansweredOnlyGroups(next);
							};
							return (
								<div key={gtaKey} className="rowSpacing">
									<Card
										title={
											gtaKey !== 'undefined' ? (
												<div className="flex flex-row items-center gap-2">
													<TaskGroupProgressCircleWithPopover groupedAnswers={groupAnswers} />
													<span>{gtaKey}</span>
												</div>
											) : null
										}
										extra={
											gtaKey !== 'undefined' ? (
												<div className="flex flex-row items-center gap-4">
													<GroupedTaskUnansweredDisplay
														groupedAnswers={groupAnswers}
														onClick={toggleUnansweredOnly}
														active={unansweredOnly}
													/>
													{scoreObject ? (
														<WalkthroughScoreDisplay scoreObject={scoreObject} withPrefix />
													) : (
														<span>--</span>
													)}
													<GroupedTaskTaskListScoreDisplay groupedAnswers={groupAnswers} />
												</div>
											) : null
										}
									>
										{unansweredOnly && (
											<div
												className="flex flex-row items-center justify-between"
												style={{
													background: '#FFFBE6',
													borderTop: '1px solid #FFE58F',
													borderBottom: '1px solid #FFE58F',
													padding: '8px 16px 8px 40px',
													margin: '-24px -24px 0 -24px',
												}}
											>
												<span className="h5regular text-character-primary">
													Showing <strong>{visibleAnswers.length}</strong> unanswered{' '}
													{visibleAnswers.length === 1 ? 'question' : 'questions'}
												</span>
												<Button type="link" onClick={disableUnansweredOnly}>
													Show All
												</Button>
											</div>
										)}
										<Table
											expandIconColumnIndex={-1}
											showHeader={false}
											pagination={false}
											loading={loading}
											columns={walkthroughColumns}
											rowKey={(el) => el.titleId}
											dataSource={visibleAnswers}
											expandedRowKeys={expandedRowKeys as any}
											onExpand={onExpand}
											expandedRowRender={expandRowRender}
											onRow={onRowWithRangeSelection((record, i) => ({
												onClick: (event) => onExpandIconClick(record.titleId),
											}))}
										/>
									</Card>
								</div>
							);
						})
					) : (
						<EmptyState
							height={120}
							headline={`Walkthrough incomplete`}
							body={
								<div style={{ maxWidth: 488, textAlign: 'center' }}>
									Walkthrough has not been conducted yet
								</div>
							}
						/>
					)}
				</Col>
			</Row>
			{showEditTaskAnswer && (
				<TaskEditPopup
					taskAnswer={currentTaskAnswer}
					onCancel={hideTaskAnswerPopup}
					onSave={updateAnswer}
					walkthrough={walkthrough}
				/>
			)}
		</Content>
	);
};

const mapStateToProps = (state) => ({
	walkthroughsFetching: state.walkthroughs.fetching,
	walkthrough: state.walkthroughs.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWalkthrough: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.readOne(id)
				: walkthroughsRestCrudThunksForBuyer.readOne(id)
		),
	updateWalkthrough: (walkthrough) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.update(walkthrough)
				: walkthroughsRestCrudThunksForBuyer.update(walkthrough)
		),
	updateTaskAnswer: (id, entity) =>
		dispatch(updateWalkthroughTaskAnswer(ownProps.userType)(id, entity)),
	markWalkthroughComplete: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markWalkthroughCompleteForSupplier(id)
				: markWalkthroughCompleteForBuyer(id)
		),
});

export default withRouter(
	withUserType(
		connect(mapStateToProps, mapDispatchToProps)(WalkthroughsDetailDetailsPage as any) as any
	)
);
